import type { ReactElement } from 'react';
import { useState } from 'react';
import { useUIDSeed } from 'react-uid';
import { toCurrency } from 'utils/numbers';
import { capitalize } from 'utils/strings';

import Plan from 'models/Plan';
import type { Extra } from 'models/Plan';

import { Component as BreakfastIcon } from 'icons/icon-mktg--breakfast.svg';
import { Component as LunchIcon } from 'icons/icon-mktg--lunch.svg';
import { Component as DinnerIcon } from 'icons/icon-mktg--dinner.svg';
import { Component as PlusIcon } from 'icons/icon-ui--plus.svg';
import { Component as CaretIcon } from 'icons/icon-ui--caret.svg';

type ExtraItemProps = {
  extra: string;
  servings: number;
  servingPrice: number;
  handleExtraChange: (extra: string | null) => void;
};

function ExtraItem({
  extra,
  servings,
  servingPrice,
  handleExtraChange,
}: ExtraItemProps): ReactElement {
  const capitalizedExtra = capitalize(extra);

  return (
    <div
      role="option"
      aria-selected="false"
      className="c-select__item-wrapper"
      tabIndex={0}
      onClick={() => handleExtraChange(extra)}
      onKeyPress={(e) => (
        e.key === 'Enter' && handleExtraChange(extra)
      )}
    >
      <div className="c-select__item">
        <div className="c-select__meal-icon">
          {
            {
              dinner: <DinnerIcon width="40" height="40" />,
              breakfast: <BreakfastIcon width="40" height="40" />,
              lunch: <LunchIcon width="40" height="40" />,
            }[extra]
          }
        </div>
        <div className="c-select__heading">
          <div className="c-select__title">
            {capitalizedExtra}
            <CaretIcon width="15" height="15" />
          </div>
          <div className="c-select__subtitle">
            {`${servings} servings (${toCurrency(servingPrice)} per serving)`}
          </div>
        </div>
      </div>
    </div>
  );
}

function AddExtraItem(): ReactElement {
  return (
    <div
      role="option"
      aria-selected="false"
      className="c-select__value is-blank"
    >
      <div className="c-select__item">
        <div className="c-select__img-wrapper">
          <PlusIcon height="9" width="9" />
        </div>
        <div className="c-select__heading">
          <div className="c-select__title">
            Add a Meal
          </div>
        </div>
      </div>
    </div>
  );
}

type Props = {
  plan: Plan;
  setPlan: (plan: Plan) => void;
  selected: Extra;
};

function AddExtrasDropdown({ plan, setPlan, selected }: Props): ReactElement {
  const { selectedExtras, availableExtras, extrasInfo } = plan;
  const [selectedExtra, setSelectedExtra] = useState(selected);
  const [isExpanded, setIsExpanded] = useState(false);
  const seed = useUIDSeed();

  const toggleExpandedIfEnterPressed = (e) => (
    e.key === 'Enter' && setIsExpanded(!isExpanded)
  );

  const unexpandIfFocusedOutside = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsExpanded(false);
    }
  };

  const handleExtraChange = (newExtra: Extra | null) => {
    const newPlan = new Plan(plan.name);
    newPlan.selectedExtras = { ...selectedExtras };

    if (newExtra !== null) {
      newPlan.selectedExtras[newExtra] += 1;
    }

    if (selectedExtra !== null) {
      newPlan.selectedExtras[selectedExtra] -= 1;
    }
    // @ts-expect-error TODO: fix null safety
    setSelectedExtra(newExtra);
    setPlan(newPlan);
  };

  const extras = Object.keys(extrasInfo).filter((extra) => (
    availableExtras[extra] - selectedExtras[extra] > 0
      && extra !== selectedExtra
  ));

  return (
    <div className="col-auto col-lg-4">
      <div className="form-group">
        <div
          className={[
            'c-select',
            'c-select--extras',
            selectedExtra === null ? 'is-blank' : '',
            isExpanded ? 'is-active' : '',
          ].join(' ')}
          role="listbox"
          aria-expanded={isExpanded}
          tabIndex={0}
          onClick={() => setIsExpanded(!isExpanded)}
          onBlur={unexpandIfFocusedOutside}
          onKeyPress={toggleExpandedIfEnterPressed}
        >
          {selectedExtra ? (
            <div
              role="option"
              aria-selected="true"
              className="c-select__value"
            >
              <ExtraItem
                extra={selectedExtra}
                servings={extrasInfo[selectedExtra].servings}
                servingPrice={extrasInfo[selectedExtra].servingPrice}
                handleExtraChange={handleExtraChange}
              />
            </div>
          ) : (
            <AddExtraItem />
          )}

          <div className="c-select__list">
            {extras.map((extra) => (
              <ExtraItem
                key={seed(extra)}
                extra={extra}
                servings={extrasInfo[extra].servings}
                servingPrice={extrasInfo[extra].servingPrice}
                handleExtraChange={handleExtraChange}
              />
            ))}

            <div
              role="option"
              aria-selected="false"
              tabIndex={0}
              className="c-select__cancel-button"
              onClick={() => handleExtraChange(null)}
              onKeyPress={toggleExpandedIfEnterPressed}
            >
              Cancel
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddExtrasDropdown;
